import React, { useState } from 'react';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import { NodeCardShell } from './NodeCardShell';
import { NodeCardProps } from './NodeCardProps';

export const AcNodeCard: React.FC<NodeCardProps> = ({
  node, index, onCommand, onEditOpen,
}) => {
  const { t } = useLanguage();
  const s = node.settings ?? {};

  // SwitchBot IR AC has no readable state and only accepts setAll (temp+mode+fan together).
  // Hold the selection locally and send all three on the Set button — one IR blast per Set,
  // not one per tap. Companion stays stateless. Power is an ACTION (Off / Set), not a state:
  // IR can't be read back, so there's no meaningful on/off state to display.
  const [temp, setTemp] = useState<number>(s.temperature ?? 22);
  const [mode, setMode] = useState<string>(s.mode ?? 'cool');
  const [fan, setFan]   = useState<string>(s.fanSpeed ?? 'auto');

  const changeTemp = (delta: number) =>
    setTemp(v => Math.min(30, Math.max(16, v + delta)));

  const turnOff = () => onCommand('turnOff');
  const send    = () => onCommand('setAll', { value: `${temp},${mode},${fan}` });

  const modes = [
    { value: 'cool', label: t.acModeCool },
    { value: 'heat', label: t.acModeHeat },
    { value: 'auto', label: t.acModeAuto },
    { value: 'dry',  label: t.acModeDry  },
    { value: 'fan',  label: t.acModeFan  },
  ];

  const fans = [
    { value: 'auto',   label: t.acFanAuto   },
    { value: 'low',    label: t.acFanLow    },
    { value: 'medium', label: t.acFanMedium },
    { value: 'high',   label: t.acFanHigh   },
  ];

  return (
    <NodeCardShell node={node} index={index} onEditOpen={onEditOpen}>
      <Typography variant="caption" color="text.secondary">{t.acMode}</Typography>
      <Box display="flex" gap={0.5} mt={0.5} mb={1.5} flexWrap="wrap">
        {modes.map(m => (
          <Chip
            key={m.value} label={m.label} size="small"
            variant={mode === m.value ? 'filled' : 'outlined'}
            onClick={() => setMode(m.value)}
          />
        ))}
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>
          {t.acTemperature}
        </Typography>
        <IconButton size="small" aria-label={t.a11yDecrease(t.acTemperature)} onClick={() => changeTemp(-1)} disabled={temp <= 16}>
          <Remove fontSize="small" />
        </IconButton>
        <Typography variant="body1" sx={{ minWidth: 52, textAlign: 'center' }}>
          {temp}°C
        </Typography>
        <IconButton size="small" aria-label={t.a11yIncrease(t.acTemperature)} onClick={() => changeTemp(1)} disabled={temp >= 30}>
          <Add fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary">{t.acFanSpeed}</Typography>
      <Box display="flex" gap={0.5} mt={0.5} mb={1.5} flexWrap="wrap">
        {fans.map(f => (
          <Chip
            key={f.value} label={f.label} size="small"
            variant={fan === f.value ? 'filled' : 'outlined'}
            onClick={() => setFan(f.value)}
          />
        ))}
      </Box>

      <Box display="flex" gap={1}>
        <Button
          variant="outlined" color="inherit" size="small" fullWidth
          onClick={turnOff} disabled={node.isLoading}
        >
          {t.statusOff}
        </Button>
        <Button
          variant="contained" size="small" fullWidth
          onClick={send} disabled={node.isLoading}
        >
          {t.acApply}
        </Button>
      </Box>
    </NodeCardShell>
  );
};
